import React, { useState, useEffect } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { Box, Avatar } from "@material-ui/core";
import Typography from "../typography";
import { RouteComponentProps } from "@reach/router";
import { useQuery } from "@apollo/client";
import { PUBLIC_MATCH_QUERY } from "../page-view-match/MatchQueries";
import { IPlayer } from "../../types";
import VideoList from "../page-view-match/VideoList";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import SportsIcon from "@material-ui/icons/Sports";

// Styled Components
const PageContainer = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  minHeight: "100vh",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ContentWrapper = styled("div")({
  maxWidth: "1200px",
  margin: "0 auto",
});

const HeaderCard = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  borderRadius: "18px",
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  color: "white",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "16px",
    padding: theme.spacing(3),
  },
}));

const LogoContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  marginBottom: "24px",
});

const Logo = styled("img")(({ theme }: IStyledArguments) => ({
  height: "60px",
  [theme.breakpoints.down("sm")]: {
    height: "50px",
  },
}));

const MatchTitle = styled(Typography.H4)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  marginBottom: theme.spacing(3),
  color: "white",
  fontWeight: 700,
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
  },
}));

const ScoreContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  background: "rgba(255, 255, 255, 0.15)",
  borderRadius: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

const TeamName = styled(Typography.H5)(({ theme }: IStyledArguments) => ({
  flex: 1,
  textAlign: "center",
  fontWeight: 700,
  color: "white",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.2rem",
  },
}));

const ScoreBox = styled(Box)({
  display: "flex",
  gap: "24px",
  alignItems: "center",
});

const Score = styled(Typography.H3)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  fontSize: "3rem",
  color: "white",
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const MatchInfo = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(3),
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
}));

const InfoChip = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "rgba(255, 255, 255, 0.9)",
  fontSize: "0.95rem",
});

const ContentCard = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.default,
  borderRadius: "16px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
    padding: theme.spacing(2),
  },
}));

const SectionHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const ScorerRow = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "8px",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateX(4px)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
}));

const PlayerInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flex: 1,
});

const PlayerAvatar = styled(Avatar)(({ theme }: IStyledArguments) => ({
  width: "48px",
  height: "48px",
  background: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "1rem",
}));

const GoalBadge = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "white",
  borderRadius: "8px",
  padding: theme.spacing(0.5, 1.5),
  fontWeight: 700,
  minWidth: "50px",
  textAlign: "center",
  fontSize: "1.1rem",
}));

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
});

const ErrorContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(6),
}));

const getPlayerInitials = (player: IPlayer): string => {
  const firstInitial = player.name?.charAt(0)?.toUpperCase() || "";
  const lastInitial = player.surname?.charAt(0)?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
};

interface PageShareMatchProps extends RouteComponentProps {
  id?: string;
}

const PageShareMatch: React.FC<PageShareMatchProps> = ({ id }) => {
  const { data, loading, error } = useQuery(PUBLIC_MATCH_QUERY, {
    variables: { id },
    skip: !id,
    // Don't use cache for public pages and handle errors gracefully
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [match, setMatch] = useState<any>();

  useEffect(() => {
    console.log("PageShareMatch - Data:", data);
    console.log("PageShareMatch - Error:", error);
    console.log("PageShareMatch - Loading:", loading);
    
    if (data?.publicMatch) {
      setMatch(data.publicMatch);
    }
  }, [data, error, loading]);

  if (loading) {
    return (
      <LoadingContainer>
        <Typography.H5>Loading match summary...</Typography.H5>
      </LoadingContainer>
    );
  }

  if (!loading && (error || !match)) {
    return (
      <PageContainer>
        <ContentWrapper>
          <ErrorContainer>
            <Typography.H4 style={{ marginBottom: "16px" }}>
              Match Not Found
            </Typography.H4>
            <Typography.Body style={{ marginBottom: "16px" }}>
              The match you're looking for doesn't exist or is no longer available.
            </Typography.Body>
            {error && (
              <Typography.Body style={{ color: "#999", fontSize: "0.85rem" }}>
                Error: {error.message}
              </Typography.Body>
            )}
            {id && (
              <Typography.Body style={{ color: "#999", fontSize: "0.85rem", marginTop: "8px" }}>
                Match ID: {id}
              </Typography.Body>
            )}
          </ErrorContainer>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // Local team names and scores (considering if local team plays as home or away)
  const localTeamName = match.isHomeTeamLocal ? match.homeTeam.name : match.awayTeam;
  const opponentTeamName = match.isHomeTeamLocal ? match.awayTeam : match.homeTeam.name;
  const localScore = match.isHomeTeamLocal ? match.score?.home || 0 : match.score?.away || 0;
  const opponentScore = match.isHomeTeamLocal ? match.score?.away || 0 : match.score?.home || 0;

  // Get scorers directly from scorerList (public query format)
  const sortedScorers = match.scorerList
    ? [...match.scorerList]
        .filter((scorer: any) => scorer.goals > 0)
        .sort((a: any, b: any) => b.goals - a.goals)
    : [];

  const totalGoals = sortedScorers.reduce(
    (acc: number, scorer: any) => acc + scorer.goals,
    0
  );

  return (
    <PageContainer>
      <ContentWrapper>
        {/* Header Card */}
        <HeaderCard>
          <LogoContainer>
            <Logo src="/images/footik-logo.svg" alt="Footik" />
          </LogoContainer>

          <MatchTitle>Match Summary</MatchTitle>

          {/* Score Display */}
          <ScoreContainer>
            <TeamName>{localTeamName}</TeamName>
            <ScoreBox>
              <Score>{localScore}</Score>
              <Typography.H5 style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                -
              </Typography.H5>
              <Score>{opponentScore}</Score>
            </ScoreBox>
            <TeamName>{opponentTeamName}</TeamName>
          </ScoreContainer>

          {/* Match Information */}
          <MatchInfo>
            <InfoChip>
              <CalendarTodayIcon fontSize="small" />
              <span>{match.date}</span>
            </InfoChip>
            {match.location && (
              <InfoChip>
                <LocationOnIcon fontSize="small" />
                <span>{match.location}</span>
              </InfoChip>
            )}
            {match.config?.numPlayers && (
              <InfoChip>
                <SportsIcon fontSize="small" />
                <span>{match.config.numPlayers} vs {match.config.numPlayers}</span>
              </InfoChip>
            )}
          </MatchInfo>
        </HeaderCard>

        {/* Goal Scorers Section */}
        {sortedScorers.length > 0 && (
          <ContentCard>
            <SectionHeader>
              <EmojiEventsIcon style={{ fontSize: "1.5rem" }} />
              <Typography.H6 style={{ margin: 0, fontWeight: 700 }}>
                Goal Scorers
              </Typography.H6>
              <Typography.Body
                style={{
                  marginLeft: "auto",
                  fontWeight: 600,
                  color: "#666",
                }}
              >
                {totalGoals} {totalGoals === 1 ? "goal" : "goals"}
              </Typography.Body>
            </SectionHeader>

            {sortedScorers.map((scorer: any, index: number) => {
              const player = scorer.player;
              return (
                <ScorerRow key={player.id}>
                  <PlayerInfo>
                    <Box position="relative">
                      <PlayerAvatar>
                        {getPlayerInitials(player)}
                      </PlayerAvatar>
                      {index === 0 && scorer.goals > 1 && (
                        <Box
                          style={{
                            position: "absolute",
                            top: "-4px",
                            right: "-4px",
                            fontSize: "1.2rem",
                          }}
                        >
                          👑
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <Typography.Body
                        style={{
                          fontWeight: 700,
                          marginBottom: "2px",
                        }}
                      >
                        {player.name} {player.surname}
                      </Typography.Body>
                      <Typography.Body
                        style={{
                          fontSize: "0.85rem",
                          color: "#666",
                        }}
                      >
                        {player.position || "Player"}
                        {player.number && ` • #${player.number}`}
                      </Typography.Body>
                    </Box>
                  </PlayerInfo>
                  <GoalBadge>
                    {scorer.goals} ⚽
                  </GoalBadge>
                </ScorerRow>
              );
            })}

            {match.ownGoals > 0 && (
              <ScorerRow style={{ background: "rgba(243, 66, 28, 0.05)" }}>
                <PlayerInfo>
                  <Avatar
                    style={{
                      backgroundColor: "#F3421C",
                      width: "48px",
                      height: "48px",
                    }}
                  >
                    OG
                  </Avatar>
                  <Box>
                    <Typography.Body style={{ fontWeight: 700 }}>
                      Own Goal
                    </Typography.Body>
                  </Box>
                </PlayerInfo>
                <GoalBadge style={{ background: "#F3421C" }}>
                  {match.ownGoals} ⚽
                </GoalBadge>
              </ScorerRow>
            )}
          </ContentCard>
        )}

        {/* Videos Section - Read Only */}
        {match.videos && match.videos.length > 0 && (
          <ContentCard>
            <VideoList matchId={id!} readonly={true} videos={match.videos} />
          </ContentCard>
        )}

        {/* Footer */}
        <Box 
          style={{ 
            textAlign: "center", 
            padding: "48px 0 32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px"
          }}
        >
          <img 
            src="/images/footik-mark.svg"
            alt="Footik"
            style={{ height: "80px", opacity: 0.8 }}
          />
          <Typography.Body style={{ color: "#666", fontSize: "0.95rem" }}>
            Powered by Footik
          </Typography.Body>
        </Box>
      </ContentWrapper>
    </PageContainer>
  );
};

export default PageShareMatch;
